import React, { useEffect, useState, useCallback } from 'react';
import { RefreshCw, ChevronDown, ChevronRight } from 'lucide-react';
import { useAdminAuth } from '../../context/AdminAuthContext';
import { listTenants, listTenantUsers, adminActivateUser, adminDeactivateUser } from '../../utils/adminApi';

export default function UsersPage() {
  const { adminKey }          = useAdminAuth();
  const [tenants, setTenants] = useState([]);
  const [expanded, setExpanded] = useState({});
  const [users, setUsers]     = useState({});
  const [loading, setLoading] = useState({});
  const [error, setError]     = useState('');
  const [actionError, setActionError] = useState('');

  // Confirmation state: { tenantId, user }
  const [toggleTarget, setToggleTarget] = useState(null);
  const [toggling, setToggling]         = useState(false);

  const loadTenants = useCallback(() => {
    setError('');
    listTenants(adminKey)
      .then(setTenants)
      .catch(err => setError(err.message));
  }, [adminKey]);

  useEffect(() => { loadTenants(); }, [loadTenants]);

  async function toggleExpand(tenantId) {
    const isOpen = expanded[tenantId];
    setExpanded(e => ({ ...e, [tenantId]: !isOpen }));

    if (!isOpen && !users[tenantId]) {
      setLoading(l => ({ ...l, [tenantId]: true }));
      try {
        const u = await listTenantUsers(adminKey, tenantId);
        setUsers(prev => ({ ...prev, [tenantId]: u }));
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(l => ({ ...l, [tenantId]: false }));
      }
    }
  }

  async function confirmToggle() {
    const { tenantId, user } = toggleTarget;
    setToggling(true);
    setActionError('');
    try {
      user.is_active
        ? await adminDeactivateUser(adminKey, tenantId, user.id)
        : await adminActivateUser(adminKey, tenantId, user.id);
      const u = await listTenantUsers(adminKey, tenantId);
      setUsers(prev => ({ ...prev, [tenantId]: u }));
      setToggleTarget(null);
    } catch (err) {
      setActionError(err.message);
      setToggleTarget(null);
    } finally {
      setToggling(false);
    }
  }

  if (error) return (
    <div className="admin-error-page">
      <p>{error}</p>
      <button className="admin-btn-secondary" onClick={loadTenants} style={{ marginTop: 12 }}>Retry</button>
    </div>
  );

  return (
    <div className="admin-page">
      <div className="admin-page-header">
        <h2 className="admin-page-title">Users</h2>
        <button className="admin-btn-icon" onClick={loadTenants} title="Refresh"><RefreshCw size={15} /></button>
      </div>
      <p style={{ color: 'var(--clr-text-secondary)', fontSize: '0.875rem', marginBottom: 24 }}>
        Click a tenant to expand its users. You can activate or deactivate any user across all tenants.
      </p>

      {actionError && (
        <div className="admin-error" style={{ marginBottom: 12, padding: '8px 12px', borderRadius: 6, background: 'var(--clr-error-bg, #fef2f2)', border: '1px solid var(--clr-error, #ef4444)' }}>
          {actionError}
        </div>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {tenants.length === 0 && (
          <p style={{ color: 'var(--clr-text-muted)', fontSize: '0.875rem' }}>No tenants found.</p>
        )}
        {tenants.map(t => (
          <div key={t.tenant_id} className="admin-tenant-group">
            <button className="admin-tenant-header" onClick={() => toggleExpand(t.tenant_id)}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                {expanded[t.tenant_id] ? <ChevronDown size={15} /> : <ChevronRight size={15} />}
                <strong>{t.name}</strong>
                <code style={{ fontSize: '0.78rem', color: 'var(--clr-text-muted)' }}>{t.tenant_id}</code>
                <span className={`admin-tag ${t.channel_id === 'direct' ? 'direct' : 'reseller'}`}>{t.channel_id}</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, fontSize: '0.82rem', color: 'var(--clr-text-secondary)' }}>
                <span>{t.user_count} / {t.max_users} users</span>
                <span className={`admin-badge ${t.is_active ? 'active' : 'inactive'}`}>{t.is_active ? 'Active' : 'Inactive'}</span>
              </div>
            </button>

            {expanded[t.tenant_id] && (
              <div className="admin-tenant-users">
                {loading[t.tenant_id] && (
                  <div style={{ padding: '12px 16px', color: 'var(--clr-text-muted)', fontSize: '0.85rem' }}>Loading…</div>
                )}
                {!loading[t.tenant_id] && users[t.tenant_id] && (
                  <table className="admin-table">
                    <thead>
                      <tr>
                        <th>Name</th><th>Email</th><th>Role</th>
                        <th>Last Login</th><th>Status</th><th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {users[t.tenant_id].length === 0 && (
                        <tr><td colSpan={6} className="admin-table-empty">No users</td></tr>
                      )}
                      {users[t.tenant_id].map(u => (
                        <tr key={u.id}>
                          <td>{u.name}</td>
                          <td>{u.email}</td>
                          <td><span className="admin-tag">{u.role}</span></td>
                          <td style={{ fontSize: '0.8rem', color: 'var(--clr-text-secondary)' }}>
                            {u.last_login
                              ? new Date(u.last_login.endsWith('Z') ? u.last_login : u.last_login + 'Z').toLocaleDateString('en-IN')
                              : <span className="admin-muted">Never</span>}
                          </td>
                          <td><span className={`admin-badge ${u.is_active ? 'active' : 'inactive'}`}>{u.is_active ? 'Active' : 'Inactive'}</span></td>
                          <td>
                            <button
                              className={`admin-btn-sm ${u.is_active ? 'danger' : 'success'}`}
                              onClick={() => setToggleTarget({ tenantId: t.tenant_id, user: u })}
                            >
                              {u.is_active ? 'Deactivate' : 'Activate'}
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Deactivate/Activate confirmation */}
      {toggleTarget && (
        <div className="admin-modal-backdrop" onClick={() => setToggleTarget(null)}>
          <div className="admin-modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 420 }}>
            <h3 className="admin-modal-title">
              {toggleTarget.user.is_active ? 'Deactivate' : 'Activate'} User
            </h3>
            <p style={{ fontSize: 13, color: 'var(--clr-text-secondary)', lineHeight: 1.6, margin: '12px 0 20px' }}>
              {toggleTarget.user.is_active
                ? <><strong>{toggleTarget.user.name}</strong> ({toggleTarget.user.email}) will no longer be able to log in.</>
                : <><strong>{toggleTarget.user.name}</strong> ({toggleTarget.user.email}) will regain access to their account.</>
              }
            </p>
            <div className="admin-form-actions">
              <button className="admin-btn-secondary" onClick={() => setToggleTarget(null)}>Cancel</button>
              <button
                className={`admin-btn-primary${toggleTarget.user.is_active ? ' danger' : ''}`}
                onClick={confirmToggle}
                disabled={toggling}
              >
                {toggling ? 'Saving…' : (toggleTarget.user.is_active ? 'Deactivate' : 'Activate')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
